/**
 * Alukap-XR + Axiome Roof Calculator — Rewritten
 * Reads from window.roofCalcData (pre-filled by Liquid section)
 * No external API calls, no tag-based filtering
 *
 * Key differences from Snapa calculator:
 * — Stage 2 has 3 selections: sheet colour, sheet thickness, bar colour
 * — Bar products (P2–P4, P8) use sku_map + colour suffix for locale-proof variant matching
 * — P7 (End Stop Bar) matched by colour + thickness + length via normalised title match
 * — P9 (Ridge Gable End Plate) matched by bar colour SKU suffix only
 *
 * Fixes vs dev implementation:
 * — Removed duplicate colour2 change handler and reset (was conflicting with barcolour handler)
 * — Fixed image URL builder to use barColourImg lookup for filename suffix (RAL_Colour not Bespoke_RAL_Colour)
 * — HTML option value for RAL must be "Bespoke RAL Colour" to match barColourSku lookup
 */

(function () {
  'use strict';

  var data = window.roofCalcData;
  if (!data) {
    console.warn('Roof calculator: no data found. Check Liquid section.');
    return;
  }

  // ─── State ───────────────────────────────────────────────────────────────
  var state = {
    tag1: '',
    rfnm: 1,
    roofKey: '',
    roofName: '',
    colour: '',          // Sheet colour — Clear / Opal / Bronze (no default)
    thickness: '',       // Sheet thickness — 10mm / 16mm / 25mm (no default)
    barColour: '',       // Bar colour — White / Brown / RAL Colour (no default)
    sizeA: 0,
    sizeB: 0,
    sheets: 0,
    cartItems: []
  };

  // Bar colour → SKU suffix map (locale-proof)
  // HTML option values must match these keys exactly (case-insensitive after toLowerCase)
  var barColourSku = {
    'white': 'W',
    'brown': 'B',
    'ral colour': 'P'
  };

  // Bar colour → image filename suffix map
  // Must match the actual filenames uploaded to Content → Files
  // e.g. Alukap-XR_Single_Slope_Free_Standing_1_Clear_RAL_Colour.webp
  var barColourImg = {
    'white': 'White',
    'brown': 'Brown',
    'ral colour': 'RAL_Colour'
  };


  // ─── Helpers ─────────────────────────────────────────────────────────────

  /**
   * Get the image filename suffix for the current bar colour.
   */
  function getBarColourImgSuffix() {
    return barColourImg[state.barColour.toLowerCase()] || 'White';
  }

  /**
   * Build the Steps 2–4 image URL from current state.
   * Naming convention: Alukap-XR_[RoofTypeName]_[Key]_[SheetColour]_[BarColourSuffix].webp
   */
  function buildImageUrl(baseUrl) {
    var img = state.tag1.replaceAll('– ', '').replace('&', 'and');
    img = img.replaceAll(' ', '_') + '_' + state.rfnm;
    return baseUrl.replace(img, 'Alukap-XR_' + img + '_' + state.colour + '_' + getBarColourImgSuffix());
  }

  /**
   * Find the correct length tier label for a given measurement.
   * Returns null if measurement exceeds all tiers (triggers max length message).
   */
  function findLengthTier(tiers, measurementMm) {
    var sorted = tiers.slice().sort(function (a, b) { return a.maxLength - b.maxLength; });
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        return sorted[i].variantLabel;
      }
    }
    return null;
  }

  /**
   * Find variant by a label appearing anywhere in the variant title.
   * Normalises both label and title: strips whitespace, converts commas to dots,
   * lowercases — handles French locale titles like "2,4 m" matching "2.4m".
   */
  function findVariantByLabel(product, label) {
    if (!product || !product.variants || !label) return null;
    var normLabel = label.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = product.variants[i].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
      if (normTitle.indexOf(normLabel) !== -1) {
        return product.variants[i];
      }
    }
    return null;
  }

  /**
   * Find variant by exact SKU match.
   * Used for bar products where sku_map provides the base SKU + colour suffix appended.
   */
  function findVariantBySku(product, sku) {
    if (!product || !product.variants || !sku) return null;
    var normSku = sku.toUpperCase();
    for (var i = 0; i < product.variants.length; i++) {
      if ((product.variants[i].sku || '').toUpperCase() === normSku) {
        return product.variants[i];
      }
    }
    return null;
  }

  /**
   * Find Axiome sheet variant by matching BOTH the calculator's sheet width
   * AND the length tier. Required for products with width × length variant titles
   * (e.g. "690mm / 3000mm"). A plain substring match on tier label "3000mm"
   * would also match "1050mm / 3000mm" variants — this enforces 690mm width.
   */
function findSheetVariant(product, tierLabel) {
  if (!product || !product.variants || !tierLabel) return null;
  var widthLabel = data.sheetWidthMm + 'mm';
  var normWidth = widthLabel.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
  var normTier = tierLabel.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();

  // Check whether any variant contains the sheet width
  // Yes → strict mode (sheet products); No → fall back to label-only match (bars, single-dimension)
  var hasWidthDimension = false;
  for (var k = 0; k < product.variants.length; k++) {
    var checkTitle = product.variants[k].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    if (checkTitle.indexOf(normWidth) !== -1) {
      hasWidthDimension = true;
      break;
    }
  }

  for (var i = 0; i < product.variants.length; i++) {
    var normTitle = product.variants[i].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    if (hasWidthDimension) {
      if (normTitle.indexOf(normWidth) !== -1 && normTitle.indexOf(normTier) !== -1) {
        return product.variants[i];
      }
    } else {
      if (normTitle.indexOf(normTier) !== -1) {
        return product.variants[i];
      }
    }
  }
  return null;
}

  /**
   * Split a measurement across available length tiers.
   * Returns array of { variantLabel, quantity } objects.
   * For measurements exceeding all tiers, fills with multiples of longest
   * then uses next size down for remainder.
   */
  function splitLength(tiers, measurementMm) {
    var sorted = tiers.slice().sort(function (a, b) { return a.maxLength - b.maxLength; });
    var results = [];
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        results.push({ variantLabel: sorted[i].variantLabel, quantity: 1 });
        return results;
      }
    }
    var longest = sorted[sorted.length - 1];
    var fullLengths = Math.floor(measurementMm / longest.maxLength);
    var remainder = measurementMm % longest.maxLength;
    if (fullLengths > 0) {
      results.push({ variantLabel: longest.variantLabel, quantity: fullLengths });
    }
    if (remainder > 0) {
      for (var j = 0; j < sorted.length; j++) {
        if (remainder <= sorted[j].maxLength) {
          results.push({ variantLabel: sorted[j].variantLabel, quantity: 1 });
          break;
        }
      }
    }
    return results;
  }

  /**
   * Get the roof_type_overrides entry for the current roof type.
   * Parses JSON string if needed.
   */
  function getOverride(rule) {
    var overrides = rule.roofTypeOverrides;
    if (typeof overrides === 'string') {
      try { overrides = JSON.parse(overrides); } catch (e) { return null; }
    }
    if (!overrides) return null;
    return overrides[state.roofKey] || null;
  }

  /**
   * Get the sku_map for a rule, parsing JSON string if needed.
   */
  function getSkuMap(rule) {
    var map = rule.skuMap;
    if (!map) return null;
    if (typeof map === 'string') {
      try { map = JSON.parse(map); } catch (e) { return null; }
    }
    return map;
  }

  /**
   * Get the correct product from a rule's products list.
   * For P1 (Axiome sheets) — multiple products split by colour × thickness.
   * Matches by SKU prefix pattern: AS[thickness][colourCode].
   * Colour codes: C = Clear, O = Opal, B = Bronze.
   * SKUs are never translated — works across all locales.
   */
  function getProduct(rule) {
    var products = rule.products;
    if (!products || products.length === 0) return null;
    if (products.length === 1) return products[0];

    var colourCode = { clear: 'C', opal: 'O', bronze: 'B' };
    var code = colourCode[state.colour.toLowerCase()] || '';
    var thicknessNum = parseInt(state.thickness) || 0; // "10mm" -> 10
    var skuPrefix = 'AS' + thicknessNum + code;

    for (var i = 0; i < products.length; i++) {
      if (products[i].variants && products[i].variants.length > 0) {
        var sku = products[i].variants[0].sku || '';
        if (sku.indexOf(skuPrefix) === 0) {
          return products[i];
        }
      }
    }
    return products[0];
  }

  /**
   * Get bar colour SKU suffix from current state.barColour.
   */
  function getBarColourSuffix() {
    return barColourSku[state.barColour.toLowerCase()] || 'W';
  }


  // ─── Core calculation ────────────────────────────────────────────────────

  function calculateKit() {
    state.cartItems = [];
    var rules = data.productRules;

    for (var r = 0; r < rules.length; r++) {
      var rule = rules[r];
      var override = getOverride(rule);
      var product = getProduct(rule);

      if (!override) continue;
      if (!product) continue;

      var formula = rule.qtyFormula;
      var variantDrivenBy = rule.variantDrivenBy;
      var quantity = 0;
      var variant = null;
      var skuMap = getSkuMap(rule);
      var colourSuffix = getBarColourSuffix();

      // ── Variant selection ─────────────────────────────────────────────

      if (variantDrivenBy === 'size_b_length_tier') {
        // P1 — Axiome Sheet: Size B drives length tier, product already resolved by colour+thickness
        // Uses findSheetVariant to enforce 690mm width (variant titles are "690mm / 3000mm" style)
        var tierLabel = findLengthTier(rule.lengthTiers, state.sizeB);
        if (!tierLabel) {
          showMaxLengthMessage();
          return;
        }
        variant = findSheetVariant(product, tierLabel);

      } else if (variantDrivenBy === 'sku_colour_suffix_size_b') {
        // P2, P3, P4 — Bar products: resolve variant by SKU (base from sku_map + colour suffix)
        // Locale-proof — no title matching needed
        var bTierLabel = findLengthTier(rule.lengthTiers, state.sizeB);
        if (!bTierLabel) {
          showMaxLengthMessage();
          return;
        }
        if (skuMap && skuMap[bTierLabel]) {
          var fullSku = skuMap[bTierLabel] + colourSuffix;
          variant = findVariantBySku(product, fullSku);
        }
        // Fallback: normalised title match if SKU not found
        if (!variant) {
          variant = findVariantByLabel(product, bTierLabel);
        }

      } else if (variantDrivenBy === 'size_a_split_length') {
        // No colour dimension — Size A split-length only
        var splits = splitLength(rule.lengthTiers, state.sizeA);
        for (var s = 0; s < splits.length; s++) {
          var splitVariant = findVariantByLabel(product, splits[s].variantLabel);
          if (splitVariant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: splitVariant,
              quantity: splits[s].quantity * (override.multiplier || 1),
              editable: true
            });
          }
        }
        continue;

      } else if (variantDrivenBy === 'colour_size_a_split') {
        // P5 — Top Wall Flashing: bar colour + Size A split-length
        // Variant title format: "[barColour] / [length]m" (e.g. "RAL Colour / 4.8m")
        // Locale-proof — normalised match handles French formatting
        var p5splits = splitLength(rule.lengthTiers, state.sizeA);
        for (var p5s = 0; p5s < p5splits.length; p5s++) {
          // Build match string with separator to require both colour AND length
          var p5MatchStr = state.barColour + ' / ' + p5splits[p5s].variantLabel;
          var p5variant = findVariantByLabel(product, p5MatchStr);
          // Fallback: try without the separator format
          if (!p5variant) {
            p5variant = findVariantByLabel(product, p5splits[p5s].variantLabel);
          }
          if (p5variant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: p5variant,
              quantity: p5splits[p5s].quantity * (override.multiplier || 1),
              editable: true
            });
          }
        }
        continue;

      } else if (variantDrivenBy === 'fixed') {
        // P6 — Flashing Tape: fixed variant by label (e.g. "150mm")
        if (rule.fixedVariantLabel) {
          variant = findVariantByLabel(product, rule.fixedVariantLabel);
        } else {
          variant = product.variants[0];
        }

      } else if (variantDrivenBy === 'colour_thickness_size_a_split') {
        // P7 — End Stop Bar: colour + thickness + length via normalised title match
        // Handles French locale ("Brun / 16 mm / 2,4 m" normalises to match "Brown/16mm/2.4m")
        var p7splits = splitLength(rule.lengthTiers, state.sizeA);
        for (var p7s = 0; p7s < p7splits.length; p7s++) {
          // Build match string: "[barColour] / [thickness] / [length]"
          var matchStr = state.barColour + ' / ' + state.thickness + ' / ' + p7splits[p7s].variantLabel;
          var p7variant = findVariantByLabel(product, matchStr);
          // Fallback: match by length only if full title match fails
          if (!p7variant) {
            p7variant = findVariantByLabel(product, p7splits[p7s].variantLabel);
          }
          if (p7variant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: p7variant,
              quantity: p7splits[p7s].quantity * (override.multiplier || 1),
              editable: true
            });
          }
        }
        continue;

      } else if (variantDrivenBy === 'sku_colour_suffix_size_a_split') {
        // P8 — Ridge Bar: Size A split-length + bar colour SKU suffix
        var p8splits = splitLength(rule.lengthTiers, state.sizeA);
        for (var p8s = 0; p8s < p8splits.length; p8s++) {
          var p8variant = null;
          if (skuMap && skuMap[p8splits[p8s].variantLabel]) {
            var p8Sku = skuMap[p8splits[p8s].variantLabel] + colourSuffix;
            p8variant = findVariantBySku(product, p8Sku);
          }
          // Fallback: normalised title match
          if (!p8variant) {
            p8variant = findVariantByLabel(product, p8splits[p8s].variantLabel);
          }
          if (p8variant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: p8variant,
              quantity: p8splits[p8s].quantity * (override.multiplier || 1),
              editable: true
            });
          }
        }
        continue;

      } else if (variantDrivenBy === 'sku_colour_suffix') {
        // P9 — Ridge Gable End Plate: bar colour SKU suffix only, no length dimension
        // SKUs: AKX772W / AKX772B / AKX772P
        for (var v = 0; v < product.variants.length; v++) {
          var vSku = (product.variants[v].sku || '').toUpperCase();
          if (vSku.slice(-1) === colourSuffix) {
            variant = product.variants[v];
            break;
          }
        }
        // Fallback: first variant
        if (!variant) variant = product.variants[0];
      }

      if (!variant) continue;

      // ── Quantity calculation ──────────────────────────────────────────

      if (formula === 'ceil_a_div_700') {
        // P1 — Axiome Sheet
        var divisor = data.sheetWidthMm + data.sheetGapMm;
        quantity = Math.ceil(state.sizeA / divisor);
        var multiplier = override.multiplier || 1;
        quantity = quantity * multiplier;
        // Store base sheet count (single slope) for sheets_minus formula downstream
        state.sheets = Math.ceil(state.sizeA / divisor);

      } else if (formula === 'sheets_minus') {
        // P2, P3, P4 — Bars: quantity relative to sheet count
        var sheetCount = state.sheets;
        var apexMultiplier = override.multiplier || 1;
        quantity = (sheetCount * apexMultiplier) - (override.sheets_minus || 0);
        if (quantity < 0) quantity = 0;

      } else if (formula === 'fixed') {
        // P3, P4, P6, P9 — Fixed qty per roof type
        quantity = override.fixed_qty || 0;

      } else if (formula === 'ceil_measurement_div_10000') {
        // P6 — Flashing Tape: ceil(measurement / 10000), min 1
        var measurement = 0;
        if (override.measurement === 'a') {
          measurement = state.sizeA;
        } else if (override.measurement === 'a_plus_b') {
          measurement = state.sizeA + state.sizeB;
        } else if (override.measurement === 'b_times_2') {
          measurement = state.sizeB * 2;
        }
        quantity = Math.ceil(measurement / 10000);
        if (quantity < 1) quantity = 1;
      }

      if (quantity > 0) {
        state.cartItems.push({
          rule: rule,
          product: product,
          variant: variant,
          quantity: quantity,
          editable: true
        });
      }
    }

    renderKitList();
  }


  // ─── Editable kit list (Step 4) ──────────────────────────────────────────

  function renderKitList() {
    var container = document.querySelector('.dkit .kit-list');
    var mcontainer = document.querySelector('.mkit .kit-list');
    if (!container) {
      console.warn('Roof calculator: .kit-list container not found');
      return;
    }

    var html = '';
    var mhtml = '';
    var total = 0;
    var totalSaved = 0;

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      var price = item.variant.price / 100;
      var compareAt = item.variant.compareAtPrice ? item.variant.compareAtPrice / 100 : null;
      var lineTotal = price * item.quantity;
      var lineSaving = compareAt ? (compareAt - price) * item.quantity : 0;
      total += lineTotal;
      totalSaved += lineSaving;

      var image = '';
      // Prefer variant-specific image (correct colour for bar products)
      // Fall back to first product image if variant has none
      if (item.variant && item.variant.featuredImage) {
        image = item.variant.featuredImage;
      } else if (item.product && item.product.images && item.product.images.length > 0) {
        image = item.product.images[0];
      }

      // Desktop layout
      html += '<div class="kit-item" data-index="' + i + '">';
      html += '  <div class="kit-item-image">';
      if (image) {
        html += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      }
      html += '  </div>';
      html += '  <div class="kit-item-details">';
      html += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      html += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      html += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        html += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        html += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        html += '    <span>' + formatMoney(price) + '</span>';
      }
      html += '  </div>';
      html += '  </div>';
      html += '  <div class="kit-item-qty">';
      html += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      html += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      html += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      html += '  </div>';
      html += '  <div class="kit-item-price">' + formatMoney(lineTotal) + '</div>';
      html += '</div>';

      // Mobile layout
      mhtml += '<div class="kit-item" data-index="' + i + '">';
      mhtml += '  <div class="w-100"><div class="w-25">';
      mhtml += '  <div class="kit-item-image">';
      if (image) {
        mhtml += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      }
      mhtml += '  </div>';
      mhtml += '  </div><div class="w-75">';
      mhtml += '  <div class="kit-item-details">';
      mhtml += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      mhtml += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      mhtml += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        mhtml += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        mhtml += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        mhtml += '    <span>' + formatMoney(price) + '</span>';
      }
      mhtml += '  </div>';
      mhtml += '  </div>';
      mhtml += '  <div class="kit-item-qty">';
      mhtml += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      mhtml += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      mhtml += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      mhtml += '  </div>';
      mhtml += '  </div></div>';
      mhtml += '</div>';
    }

    container.innerHTML = html;
    if (mcontainer) mcontainer.innerHTML = mhtml;

    // Update totals
    $('.kitcart .total span').html(formatMoney(total));

    if (totalSaved > 0) {
      var ysav = 'You saved ';
      if (window.location.pathname.indexOf('fr-fr') === 1) { ysav = 'Vous avez enregistré '; }
      $('.kitcart .saved').show().html(ysav + formatMoney(totalSaved) + '!');
    } else {
      $('.kitcart .saved').hide();
    }

    showStep('kit');
    bindKitListEvents();
  }

  function bindKitListEvents() {
    document.querySelectorAll('.kit-qty-minus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        if (state.cartItems[idx].quantity > 0) {
          state.cartItems[idx].quantity--;
          renderKitList();
        }
      });
    });

    document.querySelectorAll('.kit-qty-plus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        state.cartItems[idx].quantity++;
        renderKitList();
      });
    });

    document.querySelectorAll('.kit-qty-input').forEach(function (input) {
      input.addEventListener('change', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        var val = parseInt(this.value) || 0;
        if (val < 0) val = 0;
        state.cartItems[idx].quantity = val;
        renderKitList();
      });
    });

    document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.kit-add-to-cart').forEach(function (b) {
          b.setAttribute('disabled', 'disabled');
          if (window.location.pathname.indexOf('fr-fr') === 1) {
            b.textContent = 'Ajout au panier...';
          } else {
            b.textContent = 'Adding to cart...';
          }
        });
        addToCart();
      });
    });
  }

  function formatMoney(amount) {
    var currency = data.currency || {};
    var locale = currency.locale || 'en-GB';
    var code = currency.code || 'GBP';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code
      }).format(amount);
    } catch (e) {
      return (currency.symbol || '£') + amount.toFixed(2);
    }
  }


  // ─── Cart submission ──────────────────────────────────────────────────────

  function addToCart() {
    var items = [];

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      if (item.quantity < 1) continue;
      items.push({
        quantity: item.quantity,
        id: item.variant.id,
        properties: {
          '_ROOF TYPE': state.roofName,
          '_COLOUR': state.colour,
          '_LENGTH A': state.sizeA,
          '_LENGTH B': state.sizeB
        }
      });
    }

    if (items.length === 0) {
      alert('No items to add. Please check your quantities.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add full kit to cart';
      });
      return;
    }

    // Reverse so items appear in correct order in cart (Shopify adds in reverse)
    items.reverse();

    jQuery.post('/cart/add.js', {
      items: items
    }).then(function () {
      if (window.location.pathname.indexOf('fr-fr') === 1) {
        window.location.href = data.cartUrl || '/fr-fr/cart';
      } else {
        window.location.href = data.cartUrl || '/cart';
      }
    }).fail(function () {
      alert('There was an error adding to cart. Please try again.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add full kit to cart';
      });
    });
  }


  // ─── Max / min length messages ────────────────────────────────────────────

  function showMaxLengthMessage() {
    var msg = data.maxLengthMessage || 'This size is too long, please get free advice on Live Chat';
    alert(msg);
  }


  // ─── UI step management ───────────────────────────────────────────────────

  function showStep(step) {
    $('.rooftype, .roofsheet, .roofsize, .kit-list-wrapper').hide();
    if (step === 'rooftype') {
      $('.rooftype').show();
      updateStepIndicators('wtor');
    } else if (step === 'roofsheet') {
      $('.roofsheet').show();
      updateStepIndicators('yrc');
    } else if (step === 'roofsize') {
      $('.roofsize').show();
      updateStepIndicators('cyrs');
    } else if (step === 'kit') {
      $('.kit-list-wrapper').show();
      updateStepIndicators('kit');
    }
  }

  function updateStepIndicators(currentStep) {
    $(".roofcal .number").removeClass("current").removeClass("complete");
    var steps = ['wtor', 'yrc', 'cyrs', 'kit'];
    var currentIdx = steps.indexOf(currentStep);
    for (var i = 0; i < steps.length; i++) {
      if (i < currentIdx) {
        $(".roofcal .number." + steps[i]).addClass("complete");
      } else if (i === currentIdx) {
        $(".roofcal .number." + steps[i]).addClass("current");
      }
    }
  }


  // ─── Event bindings ───────────────────────────────────────────────────────

  // Step 1 — Roof type selection
  $(".roofcal .roofimg").click(function () {
    var roofIndex = parseInt($(this).attr('key')) - 1;
    var roofType = data.roofTypes[roofIndex];
    if (!roofType) return;

    state.roofKey = roofType.key;
    state.roofName = roofType.name;
    state.tag1 = $(this).attr('title');
    state.rfnm = $(this).attr('key');

    var baseUrl = $(this).find('img').attr('src');
    $('.roofsheet img').attr('key', baseUrl);
    $('.roofsheet img').attr('src', baseUrl);
    $('.roofsize img').attr('src', baseUrl);
    $('.qimg img').attr('src', baseUrl);

    showStep('roofsheet');
  });

  // Back to Step 1
  $('.srtype').click(function () {
    state.roofKey = '';
    state.roofName = '';
    showStep('rooftype');
  });

  // Step 2 — Sheet colour
  $('select[name="colour1"]').change(function () {
    state.colour = $(this).val();
    $('.quote .clr').text(this.options[this.selectedIndex].text);
    updateRoofImages();
  });

  // Step 2 — Bar colour
  // NOTE: HTML option values must match barColourSku keys exactly (case-insensitive)
  $('select[name="barcolour"]').change(function () {
    state.barColour = $(this).val();
    $('.quote .barclr').text(this.options[this.selectedIndex].text);
    updateRoofImages();
  });

  // Step 2 — Sheet thickness
  $('.thickness').change(function () {
    state.thickness = $(this).val();
    $('.quote .thick').text(state.thickness);
  });

  /**
   * Update the roof diagram images on Steps 2, 3, 4.
   * Only swaps to a coloured image once BOTH sheet colour AND bar colour are selected.
   * Until then, shows the original (grey) Step 1 roof type image.
   */
  function updateRoofImages() {
    var baseUrl = $('.roofsheet img').attr('key');
    if (!baseUrl) return;
    if (state.colour && state.barColour) {
      var newImage = buildImageUrl(baseUrl);
      $('.roofsheet img, .roofsize img, .qimg img').attr('src', newImage);
    } else {
      $('.roofsheet img, .roofsize img, .qimg img').attr('src', baseUrl);
    }
  }

  // Proceed to Step 3
  $('.rfsize').click(function () {
    if (!state.roofKey) {
      alert('Please select a Roof Type');
      return false;
    }
    if (!state.colour) {
      alert('Please select a Sheet Colour');
      return false;
    }
    if (!state.thickness) {
      alert('Please select a Sheet Thickness');
      return false;
    }
    if (!state.barColour) {
      alert('Please select a Bar Colour');
      return false;
    }
    showStep('roofsize');
  });

  // Back to Step 2
  $(".roofcal .yrc").click(function () {
    if (!state.roofKey) return false;
    showStep('roofsheet');
  });

  // Step 3 — Calculate kit
  $('.ckit').click(function () {
    $(this).attr('disabled', 'disabled');

    state.sizeA = parseInt($('.roofinput input[name="lengtha"]').val()) || 0;
    state.sizeB = parseInt($('.roofinput input[name="lengthb"]').val()) || 0;

    if (state.sizeA < 99 || state.sizeB < 99) {
      alert(data.minSizeMessage || 'Please enter valid length and width');
      $(this).attr('disabled', false);
      return false;
    }

    $('.quote .sizea span').text(state.sizeA);
    $('.quote .sizeb span').text(state.sizeB);

    // Derive max available Size B length from tier data
    var maxAvailableLength = 6000;
    var longestTier = 0;
    for (var r = 0; r < data.productRules.length; r++) {
      var tiers = data.productRules[r].lengthTiers;
      for (var t = 0; t < tiers.length; t++) {
        if (tiers[t].maxLength > longestTier) {
          longestTier = tiers[t].maxLength;
        }
      }
    }
    if (longestTier > 0) maxAvailableLength = longestTier;

    if (state.sizeB > maxAvailableLength) {
      showMaxLengthMessage();
      $(this).attr('disabled', false);
      return false;
    }

    calculateKit();
    $(this).attr('disabled', false);
  });

  // Re-enable calculate button when sizes change
  $(".roofinput input").change(function () {
    $(".ckit").attr("disabled", false);
  });

})();